
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, TrendingUp, FileText } from "lucide-react";

// Mock data
const forecastingData = [
  {
    id: 1,
    name: "Organic Brown Rice 1kg",
    avgWeeklySales: 12,
    stockRemaining: 45,
    forecastedDays: 26,
    suggestedReorder: 30
  },
  {
    id: 2,
    name: "Fresh Milk 1L",
    avgWeeklySales: 28,
    stockRemaining: 12,
    forecastedDays: 3,
    suggestedReorder: 40
  },
  {
    id: 3,
    name: "Whole Wheat Bread",
    avgWeeklySales: 18,
    stockRemaining: 8,
    forecastedDays: 3,
    suggestedReorder: 30
  },
  {
    id: 4,
    name: "Yogurt 500g",
    avgWeeklySales: 15,
    stockRemaining: 30,
    forecastedDays: 14,
    suggestedReorder: 25
  },
  {
    id: 5,
    name: "Instant Coffee 200g",
    avgWeeklySales: 5,
    stockRemaining: 6,
    forecastedDays: 8,
    suggestedReorder: 10
  }
];

const ForecastingTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  
  const filteredForecastingData = forecastingData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const toggleSelection = (id: number) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(itemId => itemId !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };
  
  const getForecastedDaysClass = (days: number) => {
    if (days <= 3) return "text-red-600 font-bold";
    if (days <= 7) return "text-orange-600 font-medium";
    return "";
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Forecasting & Replenishment</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              className="pl-8 w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Export Report
          </Button>
          <Button disabled={selectedProducts.length === 0}>
            Add Selected to PO ({selectedProducts.length})
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 p-4 rounded-md flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-full">
            <TrendingUp className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">AI-Powered Forecasting</h3>
            <p className="text-sm text-muted-foreground">Our AI analyzes seasonal trends, sales history, and market data to predict demand.</p>
          </div>
        </div>
        
        <div className="bg-blue-50 p-4 rounded-md flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <TrendingUp className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Intelligent Safety Stock</h3>
            <p className="text-sm text-muted-foreground">Automatically calculates optimal safety stock levels based on lead time variability.</p>
          </div>
        </div>
      </div>
      
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead className="text-center">Avg. Weekly Sales</TableHead>
              <TableHead className="text-center">Stock Remaining</TableHead>
              <TableHead className="text-center">Days Until OOS</TableHead>
              <TableHead className="text-center">Suggested Reorder</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredForecastingData.map((item) => (
              <TableRow key={item.id} className={item.forecastedDays <= 7 ? "bg-yellow-50" : ""}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell className="text-center">{item.avgWeeklySales}</TableCell>
                <TableCell className="text-center">{item.stockRemaining}</TableCell>
                <TableCell className="text-center">
                  <span className={getForecastedDaysClass(item.forecastedDays)}>
                    {item.forecastedDays}
                  </span>
                </TableCell>
                <TableCell className="text-center">{item.suggestedReorder}</TableCell>
                <TableCell className="text-right">
                  <Button 
                    size="sm" 
                    variant={selectedProducts.includes(item.id) ? "default" : "outline"}
                    onClick={() => toggleSelection(item.id)}
                  >
                    {selectedProducts.includes(item.id) ? "Added to PO" : "Add to PO"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ForecastingTab;
